
import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Euro, Package, Loader } from 'lucide-react';
import { API_BASE } from '../../config';

// --- Simple Chart Components (Pure SVG/CSS to replace Recharts) ---

const SimplePieChart = ({ data, colors }) => {
  const total = data.reduce((acc, d) => acc + d.value, 0);
  let accumulatedAngle = 0;

  const paths = data.map((entry, index) => {
    const percentage = entry.value / total;
    const angle = percentage * 360;
    const startAngle = accumulatedAngle;
    const endAngle = accumulatedAngle + angle;
    accumulatedAngle += angle;

    // Convert polar to cartesian
    const x1 = 50 + 50 * Math.cos(Math.PI * startAngle / 180);
    const y1 = 50 + 50 * Math.sin(Math.PI * startAngle / 180);
    const x2 = 50 + 50 * Math.cos(Math.PI * endAngle / 180);
    const y2 = 50 + 50 * Math.sin(Math.PI * endAngle / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;
    const pathData = `M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return (
      <g key={index}>
        <path d={pathData} fill={colors[index % colors.length]} stroke="white" strokeWidth="1" />
      </g>
    );
  });

  return (
    <div className="flex items-center justify-center space-x-8">
        <svg viewBox="0 0 100 100" className="w-48 h-48 transform -rotate-90">
            {paths}
        </svg>
        <div className="space-y-2 text-xs">
            {data.map((entry, index) => (
                <div key={index} className="flex items-center">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors[index % colors.length] }}></span>
                    <span className="text-gray-600 font-medium">{entry.name}</span>
                    <span className="text-gray-400 ml-2">({((entry.value / total) * 100).toFixed(0)}%)</span>
                </div>
            ))}
        </div>
    </div>
  );
};

const SimpleBarChart = ({ data, colors }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1);
    
    return (
        <div className="h-64 flex items-end justify-between space-x-2 pt-6">
            {data.map((entry, index) => (
                <div key={index} className="flex-1 flex flex-col items-center group relative h-full justify-end">
                    <div className="w-full bg-gray-100 rounded-t-md relative flex flex-col justify-end overflow-hidden h-full group">
                        <div 
                            className="w-full transition-all duration-500 ease-out"
                            style={{ height: `${(entry.value / maxValue) * 100}%`, backgroundColor: colors[index % colors.length] }}
                        ></div>
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {entry.value}
                        </div>
                    </div>
                    <span className="text-[10px] text-gray-500 mt-2 truncate w-full text-center block max-w-[60px]" title={entry.name}>
                        {entry.name}
                    </span>
                </div>
            ))}
        </div>
    );
};

const SimpleLineChart = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1);
    const points = data.map((d, i) => {
        const x = (i / (data.length - 1)) * 100;
        const y = 100 - ((d.value / maxValue) * 100);
        return `${x},${y}`;
    }).join(' ');

    return (
        <div className="h-64 w-full relative pt-4">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full overflow-visible">
                {/* Grid lines */}
                <line x1="0" y1="25" x2="100" y2="25" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2" />
                <line x1="0" y1="50" x2="100" y2="50" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2" />
                <line x1="0" y1="75" x2="100" y2="75" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2" />
                
                {/* Line */}
                <polyline points={points} fill="none" stroke="#8b5cf6" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                
                {/* Points */}
                {data.map((d, i) => {
                    const x = (i / (data.length - 1)) * 100;
                    const y = 100 - ((d.value / maxValue) * 100);
                    return (
                        <circle key={i} cx={x} cy={y} r="3" fill="#8b5cf6" className="hover:r-4 transition-all cursor-pointer">
                            <title>{d.label}: {d.value}</title>
                        </circle>
                    );
                })}
            </svg>
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                {data.map((d, i) => <span key={i}>{d.label}</span>)}
            </div>
        </div>
    );
};

export default function AdminStats({ t }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    revenue: 0,
    byService: [],
    byMonth: [],
    byDay: []
  });

  const COLORS = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];

  useEffect(() => {
    const fetchData = async () => {
        try {
          const [bookingsRes, servicesRes] = await Promise.all([
            fetch(`${API_BASE}/bookings`),
            fetch(`${API_BASE}/services`)
          ]);
    
          const bookingsData = await bookingsRes.json();
          const servicesData = await servicesRes.json();
    
          calculateStats(bookingsData, servicesData);
          setLoading(false);
        } catch (err) {
          console.error('Erreur chargement données:', err);
          setLoading(false);
        }
      };
    
      const calculateStats = (bookingsData, servicesData) => {
        // Créer un map des services pour faciliter la recherche
        const serviceMap = {};
        servicesData.forEach(s => {
          // Normalisation des noms pour éviter les doublons dus à la casse/espaces
          if(s.name_fr) serviceMap[s.name_fr] = s;
          if(s.name_en) serviceMap[s.name_en] = s;
          if(s.name_pt) serviceMap[s.name_pt] = s;
          if(s.name) serviceMap[s.name] = s;
        });
    
        // Stats par service
        const serviceCount = {};
        const serviceRevenue = {};
        let totalRevenue = 0;
    
        bookingsData.forEach(booking => {
          if (booking.status === 'cancelled') return;
    
          const serviceName = booking.service_type || 'Autre';
          
          // Compter les réservations
          serviceCount[serviceName] = (serviceCount[serviceName] || 0) + 1;
          
          // Calculer le revenu
          const service = serviceMap[serviceName];
          // Fallback au prix moyen si service non trouvé ou prix manquant
          const price = service && service.price ? parseFloat(service.price) : 0;
          
          serviceRevenue[serviceName] = (serviceRevenue[serviceName] || 0) + price;
          totalRevenue += price;
        });
    
        const byService = Object.keys(serviceCount).map(name => ({
          name,
          value: serviceCount[name], // for Charts
          count: serviceCount[name],
          revenue: serviceRevenue[name] || 0
        })).sort((a, b) => b.count - a.count);
    
        // Stats par mois
        const monthCount = {};
        bookingsData.forEach(booking => {
          if (booking.booking_date && booking.status !== 'cancelled') {
            const date = new Date(booking.booking_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthCount[monthKey] = (monthCount[monthKey] || 0) + 1;
          }
        });
    
        const byMonth = Object.keys(monthCount)
          .sort()
          .slice(-6) // 6 derniers mois
          .map(key => {
            const [, month] = key.split('-');
            const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
            return {
              label: monthNames[parseInt(month) - 1],
              value: monthCount[key]
            };
          });
    
        // Stats par jour de la semaine
        const dayCount = {};
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        
        bookingsData.forEach(booking => {
          if (booking.booking_date && booking.status !== 'cancelled') {
            const date = new Date(booking.booking_date);
            const dayName = dayNames[date.getDay()];
            dayCount[dayName] = (dayCount[dayName] || 0) + 1;
          }
        });
    
        // Ordonner: Lun -> Dim
        const orderedDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        const byDay = orderedDays.map(day => ({
          name: day,
          value: dayCount[day] || 0
        }));
    
        setStats({
          total: bookingsData.filter(b => b.status !== 'cancelled').length,
          revenue: totalRevenue,
          byService,
          byMonth,
          byDay
        });
      };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="animate-spin h-12 w-12 text-pink-600" />
      </div>
    );
  }

  // Traductions sécurisées
  const ST = t.admin.stats || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 animate-fade-in-up">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{ST.title || "Statistiques des Prestations"}</h1>
          <p className="text-gray-600">{ST.subtitle || "Analyse des réservations et performances"}</p>
        </div>

        {/* Cards résumé */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-pink-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">{ST.totalBookings || "Total Réservations"}</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.total}</p>
              </div>
              <div className="p-3 bg-pink-50 rounded-full">
                <Calendar className="h-8 w-8 text-pink-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">{ST.totalRevenue || "Revenu Total"}</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.revenue.toFixed(2)}€</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-full">
                <Euro className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">{ST.activeServices || "Services Actifs"}</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.byService.length}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          {/* Répartition par service - Camembert */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-pink-600" />
              {ST.pieTitle || "Répartition par Service"}
            </h3>
            <SimplePieChart data={stats.byService} colors={COLORS} />
          </div>

          {/* Nombre de réservations par service - Barres */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-6">{ST.barTitle || "Popularité des Services"}</h3>
            <SimpleBarChart data={stats.byService.map(s => ({ name: s.name, value: s.count }))} colors={COLORS} />
          </div>

          {/* Évolution mensuelle */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-6">{ST.lineTitle || "Évolution Mensuelle"}</h3>
            <SimpleLineChart data={stats.byMonth} />
          </div>

          {/* Répartition par jour de la semaine */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-6">{ST.dayTitle || "Activité par Jour"}</h3>
            <SimpleBarChart data={stats.byDay} colors={['#3b82f6']} />
          </div>
        </div>

        {/* Tableau détaillé des services */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-6">{ST.detailsTitle || "Détails par Service"}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 uppercase text-xs">{ST.colService || "Service"}</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 uppercase text-xs">{ST.colCount || "Nombre"}</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-600 uppercase text-xs">{ST.colRevenue || "Revenu"}</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-600 uppercase text-xs">{ST.colAvg || "Moy./Service"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.byService.map((service, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-800">{service.name}</td>
                    <td className="py-3 px-4 text-sm text-center text-gray-600">{service.count}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">{service.revenue.toFixed(2)}€</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {(service.revenue / service.count).toFixed(2)}€
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                  <td className="py-3 px-4 text-sm">{ST.total || "TOTAL"}</td>
                  <td className="py-3 px-4 text-sm text-center">{stats.total}</td>
                  <td className="py-3 px-4 text-sm text-right">{stats.revenue.toFixed(2)}€</td>
                  <td className="py-3 px-4 text-sm text-right">
                    {stats.total > 0 ? (stats.revenue / stats.total).toFixed(2) : '0.00'}€
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
